import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import IconComponent from "../../Icons/IconComponent";
import * as BrokerStyles from "../../Broker/Styles.js";
import { buildCreateUserPayload } from "./helpers.js";
import * as styles from "./Styles.js";

const INITIAL_FORM = {
  id: "",
  email: "",
  password: "",
  username: "",
  enable: true,
  super: false,
};

function getInitialForm(user) {
  if (!user) return INITIAL_FORM;

  return {
    id: user.id ?? "",
    email: user.email ?? "",
    password: user.password ?? "",
    username: user.username ?? "",
    enable: Boolean(user.enable),
    super: Boolean(user.super),
  };
}

function OptionCard({ selected, label, onClick }) {
  return (
    <Box onClick={onClick} sx={styles.optionCard(selected)}>
      <Box sx={styles.optionRadio(selected)}>
        {selected && <Box sx={styles.optionRadioDot} />}
      </Box>

      <Typography sx={{ fontWeight: 400 }}>{label}</Typography>
    </Box>
  );
}

function RequiredLabel({ children }) {
  return (
    <Box sx={styles.requiredLabel}>
      <Typography sx={{ fontWeight: 400 }}>{children}</Typography>
      <Typography component="span" sx={styles.requiredMark}>
        *
      </Typography>
    </Box>
  );
}

export function CreateAdminUserModal({ open, onClose, onSave, user }) {
  const [form, setForm] = useState(getInitialForm(user));
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(user);

  const isValid =
    String(form.email || "").trim() &&
    (isEditMode || String(form.password || "").trim()) &&
    String(form.username || "").trim();

  useEffect(() => {
    if (!open) return;

    setForm(getInitialForm(user));
    setShowPassword(false);
    setSubmitting(false);
  }, [open, user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    if (submitting) return;
    setForm(getInitialForm(user));
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!isValid || submitting) return;

    try {
      setSubmitting(true);
      const saved = await onSave?.(buildCreateUserPayload(form));

      if (saved === false) return;

      setForm(getInitialForm(user));
      onClose?.();
    } catch (error) {
      console.error("error creating admin user:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : handleCancel}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        {isEditMode ? "Edit user" : "Create a user"}
        <Box
          onClick={submitting ? undefined : handleCancel}
          sx={styles.dialogCloseButton(submitting)}
        >
          x
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <RequiredLabel>Email</RequiredLabel>
            <TextField
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
              placeholder="Enter user's email"
              sx={styles.roundedInput}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <RequiredLabel>Username</RequiredLabel>
            <TextField
              name="username"
              value={form.username}
              onChange={handleChange}
              fullWidth
              required
              placeholder="Enter username"
              sx={styles.roundedInput}
              disabled={submitting}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <RequiredLabel>Password</RequiredLabel>
            <TextField
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              fullWidth
              required={!isEditMode}
              placeholder={
                isEditMode ? "Enter a new password" : "Enter a password"
              }
              sx={styles.roundedInput}
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={styles.passwordToggle(submitting)}
                      onClick={() => !submitting && setShowPassword((v) => !v)}
                    >
                      <IconComponent
                        icon={showPassword ? "visibility_off" : "visibility"}
                        size="16px"
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          <RequiredLabel>Status</RequiredLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <OptionCard
                label="Enabled"
                selected={form.enable === true}
                onClick={() => !submitting && handleSelect("enable", true)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <OptionCard
                label="Disabled"
                selected={form.enable === false}
                onClick={() => !submitting && handleSelect("enable", false)}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <RequiredLabel>Role</RequiredLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <OptionCard
                label="Super"
                selected={form.super === true}
                onClick={() => !submitting && handleSelect("super", true)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <OptionCard
                label="Standard"
                selected={form.super === false}
                onClick={() => !submitting && handleSelect("super", false)}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={styles.dialogActions}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          disabled={submitting}
          sx={{ ...BrokerStyles.ExportButton, ...styles.dialogActionButton }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          variant="contained"
          sx={{
            ...BrokerStyles.ExportButton,
            ...BrokerStyles.ExportButtonRed,
            ...styles.dialogActionButton,
          }}
        >
          {submitting ? "Processing..." : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
